import React, { useState, useEffect } from 'react';
import { FileText, Upload, Trash2, Sparkles, Send, Copy, BookOpen, Loader, AlertCircle } from 'lucide-react';
import { extractTextFromPdf, chunkPageText, retrieve, buildCitedContext, type Document, type DocChunk } from './lib/engine';
import { aiAnswerWithCitations } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

const STORAGE = 'ca_documind_v1';

interface QA { id: string; question: string; cited: DocChunk[]; answer: string; createdISO: string; }

export default function App() {
  const [docs, setDocs] = useState<Document[]>(() => JSON.parse(localStorage.getItem(STORAGE) || '[]'));
  const [history, setHistory] = useState<QA[]>(() => JSON.parse(localStorage.getItem(STORAGE + '_qa') || '[]'));
  const [question, setQuestion] = useState('');
  const [aiOutput, setAiOutput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [activeCited, setActiveCited] = useState<DocChunk[]>([]);

  useEffect(() => { localStorage.setItem(STORAGE, JSON.stringify(docs)); }, [docs]);
  useEffect(() => { localStorage.setItem(STORAGE + '_qa', JSON.stringify(history)); }, [history]);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []); if (files.length === 0) return;
    setImporting(true); setError('');
    for (const file of files) {
      try {
        const docId = crypto.randomUUID();
        let chunks: DocChunk[] = [];
        let totalChars = 0;
        if (file.name.toLowerCase().endsWith('.pdf')) {
          const { pageTexts, totalChars: tc } = await extractTextFromPdf(file);
          totalChars = tc;
          pageTexts.forEach((text, ix) => { chunks.push(...chunkPageText(text, ix + 1, docId, file.name)); });
        } else {
          const text = await file.text();
          totalChars = text.length;
          chunks.push(...chunkPageText(text, 1, docId, file.name));
        }
        const doc: Document = { id: docId, name: file.name, type: file.name.toLowerCase().endsWith('.pdf') ? 'pdf' : file.name.endsWith('.md') ? 'md' : 'txt', chunks, totalChars, addedISO: new Date().toISOString() };
        setDocs(prev => [doc, ...prev]);
      } catch (err) { setError(`${file.name}: ${err instanceof Error ? err.message : 'extraction failed'}`); }
    }
    setImporting(false);
    e.target.value = '';
  }

  function deleteDoc(id: string) { setDocs(docs.filter(d => d.id !== id)); }

  async function handleAsk() {
    if (!question.trim()) return;
    const allChunks = docs.flatMap(d => d.chunks);
    if (allChunks.length === 0) { setError('Upload a document first.'); return; }
    const cited = retrieve(allChunks, question, 6);
    setActiveCited(cited);
    if (cited.length === 0) { setAiOutput('No matching passages found. Try a different question or upload more documents.'); return; }
    setAiLoading(true); setAiOutput('');
    let t = '';
    await aiAnswerWithCitations(question, buildCitedContext(cited), (chunk) => { t += chunk; setAiOutput(t); });
    setAiLoading(false);
    setHistory(h => [{ id: crypto.randomUUID(), question, cited, answer: t, createdISO: new Date().toISOString() }, ...h].slice(0, 50));
  }

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0]">
      <header className="border-b border-white/10 bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold text-lg">C</div><div><h1 className="text-lg font-semibold tracking-tight">CatalystAgile <span className="text-[#d4af37]">DocuMind</span></h1><p className="text-xs text-[#8a9bb8]">PDF/text Q&A · cited answers · runs in-browser</p></div></div>
          <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-5">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Upload className="w-4 h-4"/> Add documents</h2>
            <label className="block w-full px-3 py-4 rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-dashed border-white/20 text-center text-xs font-semibold cursor-pointer">{importing ? <span className="flex items-center justify-center gap-2"><Loader className="w-4 h-4 animate-spin"/> Extracting...</span> : <>📄 Drop PDF / TXT / MD here<br/><span className="text-[10px] text-[#8a9bb8] font-normal mt-1 block">Multi-file OK · stays in browser</span></>}<input type="file" accept=".pdf,.txt,.md" multiple className="hidden" onChange={handleUpload} disabled={importing}/></label>
            {error && <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-red-300 text-xs flex items-start gap-2"><AlertCircle className="w-3 h-3 mt-0.5"/> {error}</div>}
          </div>
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-5">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><FileText className="w-4 h-4"/> Library ({docs.length})</h2>
            {docs.length === 0 && <p className="text-xs text-[#8a9bb8]">No documents yet.</p>}
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {docs.map(d => (
                <div key={d.id} className="p-2 rounded text-xs bg-[#132238]">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0"><div className="font-semibold truncate">{d.name}</div><div className="text-[10px] text-[#8a9bb8]">{d.chunks.length} chunks · {Math.round(d.totalChars/1000)}k chars</div></div>
                    <button onClick={() => deleteDoc(d.id)} className="text-[#8a9bb8] hover:text-red-400"><Trash2 className="w-3 h-3"/></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {history.length > 0 && (
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-5">
              <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4"/> History ({history.length})</h2>
              <div className="space-y-1 max-h-[260px] overflow-y-auto">{history.map(q => <div key={q.id} onClick={() => { setQuestion(q.question); setActiveCited(q.cited); setAiOutput(q.answer); }} className="p-2 rounded text-xs cursor-pointer bg-[#132238] hover:bg-[#1a2d4a]"><div className="truncate">{q.question}</div><div className="text-[10px] text-[#8a9bb8]">{new Date(q.createdISO).toLocaleString()}</div></div>)}</div>
            </div>
          )}
        </aside>

        <section className="lg:col-span-8 space-y-4">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-5">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3">Ask anything across your library</h2>
            <div className="flex gap-2">
              <input value={question} onChange={e => setQuestion(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAsk(); }} placeholder="e.g. What does the contract say about IP ownership?" className="flex-1 px-3 py-3 text-sm bg-[#132238] rounded-md border border-white/10 focus:border-[#d4af37] outline-none"/>
              <button onClick={handleAsk} disabled={aiLoading || !question.trim() || docs.length === 0} className="px-4 rounded-md bg-[#d4af37] text-[#0a1628] font-semibold hover:bg-[#e8c547] disabled:opacity-50 flex items-center gap-2"><Send className="w-4 h-4"/> {aiLoading ? 'Thinking...' : 'Ask'}</button>
            </div>
            <p className="text-[10px] text-[#8a9bb8] mt-2">Step 1 (free): in-browser keyword retrieval finds the top 6 relevant passages. Step 2 (Groq key): AI synthesizes a cited answer.</p>
          </div>

          {activeCited.length > 0 && (
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-5">
              <h3 className="text-sm font-semibold text-[#d4af37] mb-3">Retrieved passages</h3>
              <div className="space-y-2">{activeCited.map((c, i) => (
                <div key={c.id} className="bg-[#132238] rounded-md p-3">
                  <div className="text-[10px] text-[#d4af37] font-mono mb-1">[{i + 1}] {c.docName} · page {c.page}</div>
                  <div className="text-xs text-[#f0f0f0]">{c.text}</div>
                </div>
              ))}</div>
            </div>
          )}

          {aiOutput && (
            <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/30 p-5">
              <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-semibold text-[#d4af37] flex items-center gap-2"><Sparkles className="w-4 h-4"/> Answer</h3><button onClick={() => navigator.clipboard.writeText(aiOutput)} className="text-xs text-[#d4af37] hover:text-[#e8c547] inline-flex items-center gap-1"><Copy className="w-3 h-3"/> Copy</button></div>
              <div className="p-3 bg-[#132238] rounded-md text-sm whitespace-pre-wrap">{aiOutput}</div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
